// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Cart popup  (opened from the cart icon in components/Navbar)
// "Your Fresh Catch Cart": a bottom sheet on phones, a right-hand drawer from `sm` up. Lists every pack in the cart with
// its cut ("Edit cut" opens that catch's page, /menu/:id), quantity steppers and remove, then the bill (items + the flat
// delivery fee from data/delivery.ts = the total before any welcome coupon, which checkout applies) and "Proceed to Checkout".
// Modal behaviour: focus moves in, Tab is trapped, Escape/backdrop/✕ close, focus returns to the cart icon, and the page
// scroll lock is owned by Navbar. Deliberately NOT here: delivery-time promises, savings, cold-chain claims.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Pencil, Plus, ShoppingBag, Trash2, Truck, X } from "lucide-react";
import useCommercialRules from "../hooks/useCommercialRules";
import { deliveryFeeFor } from "../utils/finance";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import useCart from "../hooks/useCart";
import { formatInr } from "../utils/priceFormat";

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

export default function CartDrawer({ open, onClose }: CartDrawerProps) {
  const { lines, subtotal, removeItem, setQty } = useCart();
  const rules = useCommercialRules();
  const fee = deliveryFeeFor(subtotal, null, rules);
  const navigate = useNavigate();
  const reducedMotion = usePrefersReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus({ preventScroll: true });
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key !== "Tab" || !panelRef.current) return;
      const nodes = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      opener?.focus?.({ preventScroll: true });
    };
  }, [open, onClose]);

  const goToCheckout = () => {
    onClose();
    navigate("/checkout");
  };

  const stepper = "flex h-9 w-9 items-center justify-center rounded-full bg-foam-100 text-ocean-800 hover:bg-ocean-900/10 dark:bg-white/10 dark:text-foam-100";

  // Portalled to <body>: the navbar's backdrop-blur makes it the containing block of any `fixed` child, which would
  // collapse this overlay to the navbar's height.
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.15 }}
          className="fixed inset-x-0 bottom-0 top-8 z-[60] flex items-end bg-ocean-950/70 backdrop-blur-sm sm:items-stretch sm:justify-end"
          onClick={onClose}
        >
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cart-title"
            initial={reducedMotion ? false : { y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: reducedMotion ? 0 : 0.25, ease: "easeInOut" }}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[90%] w-full flex-col rounded-t-3xl bg-white shadow-2xl dark:bg-ocean-900 sm:h-full sm:max-h-full sm:max-w-md sm:rounded-none"
          >
            <div className="flex shrink-0 items-center justify-between px-5 pb-3 pt-5">
              <div>
                <h2 id="cart-title" className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">Your Fresh Catch Cart</h2>
                {lines.length > 0 && (
                  <p className="text-xs text-ocean-800/75 dark:text-foam-100/70">
                    {lines.length} {lines.length === 1 ? "pack" : "packs"} · {new Set(lines.map((line) => line.item.id)).size} {new Set(lines.map((line) => line.item.id)).size === 1 ? "catch" : "catches"}
                  </p>
                )}
              </div>
              <button
                type="button"
                data-autofocus
                onClick={onClose}
                aria-label="Close cart"
                className="flex h-11 w-11 items-center justify-center rounded-full text-ocean-800/70 hover:bg-ocean-900/5 focus-visible:outline-2 focus-visible:outline-teal-600 dark:text-foam-100/70 dark:hover:bg-white/10"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="min-h-0 grow overflow-y-auto px-5 pb-4">
              {lines.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <ShoppingBag className="h-10 w-10 text-ocean-800/30 dark:text-foam-100/30" aria-hidden="true" />
                  <p className="mt-3 font-semibold text-ocean-950 dark:text-foam-50">Your cart is empty.</p>
                  <Link
                    to="/menu"
                    onClick={onClose}
                    className="mt-4 inline-flex min-h-11 items-center rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700"
                  >
                    Browse the catch
                  </Link>
                </div>
              ) : (
                <>
                  <p className="flex items-center gap-2 rounded-xl bg-teal-100 px-3 py-2 text-xs font-semibold text-teal-700">
                    <Truck className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {rules.freeDeliveryAbove !== null ? `Delivery ${formatInr(rules.deliveryFee)} per order — free above ${formatInr(rules.freeDeliveryAbove)}.` : `Flat ${formatInr(rules.deliveryFee)} delivery on every order.`}
                  </p>

                  <ul className="mt-4 space-y-4">
                    {lines.map((line) => (
                      <li key={`${line.item.id}-${line.pack.id}`} className="flex gap-3">
                        <img src={line.item.image} alt="" width={64} height={64} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold leading-snug text-ocean-950 dark:text-foam-50">{line.item.name}</p>
                            <span className="shrink-0 font-semibold text-ocean-950 dark:text-foam-50">{formatInr(line.lineTotal)}</span>
                          </div>
                          <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">
                            {line.pack.label} pack · {formatInr(line.unitPrice)} each
                          </p>
                          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs font-semibold text-teal-700 dark:text-teal-300">
                            Cut: {line.cut.name}
                            <Link
                              to={`/menu/${line.item.id}`}
                              onClick={onClose}
                              className="inline-flex items-center gap-1 underline underline-offset-2"
                            >
                              <Pencil className="h-3 w-3" aria-hidden="true" />
                              Edit cut<span className="sr-only"> for {line.item.name}</span>
                            </Link>
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setQty(line.item.id, line.pack.id, line.qty - 1)}
                              aria-label={`Decrease ${line.item.name} ${line.pack.label} quantity`}
                              className={stepper}
                            >
                              <Minus className="h-4 w-4" aria-hidden="true" />
                            </button>
                            <span aria-label={`${line.qty} packs`} className="w-6 text-center text-sm font-bold text-ocean-950 dark:text-foam-50">{line.qty}</span>
                            <button
                              type="button"
                              onClick={() => setQty(line.item.id, line.pack.id, line.qty + 1)}
                              aria-label={`Increase ${line.item.name} ${line.pack.label} quantity`}
                              className={stepper}
                            >
                              <Plus className="h-4 w-4" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeItem(line.item.id, line.pack.id)}
                              aria-label={`Remove ${line.item.name} ${line.pack.label} from cart`}
                              className="ml-auto flex h-9 w-9 items-center justify-center rounded-full text-coral-500 hover:bg-coral-400/10 dark:text-coral-300"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <dl className="mt-5 space-y-1.5 rounded-2xl bg-foam-100 p-4 text-sm dark:bg-white/5">
                    <div className="flex justify-between">
                      <dt className="text-ocean-800/80 dark:text-foam-100/75">Item total</dt>
                      <dd className="font-semibold text-ocean-950 dark:text-foam-50">{formatInr(subtotal)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-ocean-800/80 dark:text-foam-100/75">Delivery fee</dt>
                      <dd className="font-semibold text-ocean-950 dark:text-foam-50">{fee === 0 ? "Free" : formatInr(fee)}</dd>
                    </div>
                    <div className="flex justify-between border-t border-ocean-900/10 pt-2 dark:border-white/10">
                      <dt className="font-bold text-ocean-950 dark:text-foam-50">Grand total</dt>
                      <dd className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{formatInr(subtotal + fee)}</dd>
                    </div>
                    <p className="pt-1 text-xs text-ocean-800/70 dark:text-foam-100/65">A welcome coupon, if you have one, is applied at checkout.</p>
                  </dl>
                </>
              )}
            </div>

            {lines.length > 0 && (
              <div className="shrink-0 border-t border-ocean-900/10 px-5 py-4 dark:border-white/10">
                <button
                  type="button"
                  onClick={goToCheckout}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-ocean-800 text-sm font-bold text-white hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                >
                  Proceed to Checkout · {formatInr(subtotal + fee)} →
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
